import { isPDFEbookType, isPubhubEbookType, isLCPEbookType } from 'utils/assets';
import { getMediaKey } from './index';
import { probeTransformersManifest } from './probe-transformers-reader';
import { extractTransformersUidFromMediaKey } from './media.utils';

export const READER_KIND = Object.freeze({
  pdf: 'pdf',
  pubhub: 'pubhub',
});

const DEFAULT_LCP_READER_KIND = READER_KIND.pubhub;

const isDirectPdfMediaKey = (key) => typeof key === 'string' && /\.pdf($|\?|#)/i.test(key);

/**
 * Chooses PDFReader vs PubhubReader for the active queue item.
 *
 * Sync paths: catalog already exposes application/pdf or application/epub+zip.
 * Async path: LCP license MIME — publication type is unknown until we resolve downloadUrl.
 */
export const resolveEbookReaderKind = async (queueItem) => {
  if (isPDFEbookType(queueItem)) {
    return READER_KIND.pdf;
  }
  if (isPubhubEbookType(queueItem)) {
    return READER_KIND.pubhub;
  }
  if (!isLCPEbookType(queueItem)) {
    // Unsupported ebook shape (e.g. pubhub-order-id) — no reader
    return null;
  }

  const { downloadUrl } = queueItem ?? {};
  if (!downloadUrl) {
    return DEFAULT_LCP_READER_KIND;
  }

  try {
    const { key } = await getMediaKey(downloadUrl);

    if (extractTransformersUidFromMediaKey(key)) {
      // EPUB: manifest.json is available. PDF: manifest path is rejected or absent — use PDFReader
      // (same assumption as pdf-reader.component.js for transformers keys).
      const hasEpubManifest = await probeTransformersManifest(key);
      return hasEpubManifest ? READER_KIND.pubhub : READER_KIND.pdf;
    }

    // LCP PDF served as a direct file URL (not transformers) — PDFReader loads key as file URL
    if (isDirectPdfMediaKey(key)) {
      return READER_KIND.pdf;
    }

    return DEFAULT_LCP_READER_KIND;
  } catch {
    return DEFAULT_LCP_READER_KIND;
  }
};
